/**
 * clipboard.js
 * Getting pictures off the clipboard, the same way on every screen.
 *
 * Pasting is fiddlier than it looks, and the awkward parts are the reason this
 * is shared rather than written out three times:
 *
 * **A paste arrives on the window, not on an element.** There is no way to
 * scope it to a drop zone, so every screen that wants it listens globally and
 * decides for itself whether to take it.
 *
 * **Ctrl+V needs the page to have focus**, which is exactly what has just been
 * lost if you came from a screenshot tool. So a button that reads the
 * clipboard directly is offered alongside, and it does not care where the
 * cursor is. Chrome asks permission the first time; Firefox refuses outright,
 * and the caller is told to use Ctrl+V instead rather than left wondering.
 *
 * **A screenshot arrives with no name, or "image.png".** Forty of those in a
 * library are indistinguishable, so anything unnamed gets a dated one.
 */

/** "pasted-2026-09-11-01-22-04.png" — legible in the library months later. */
export function pastedName(type) {
  const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
  const ext = (String(type).split('/')[1] || 'png').replace('jpeg', 'jpg').split('+')[0];
  return `pasted-${stamp}.${ext}`;
}

/** Anything unnamed, or named by the browser's default, gets a real name. */
export const named = (file) =>
  file.name && file.name !== 'image.png'
    ? file
    : new File([file], pastedName(file.type), { type: file.type });

/**
 * Every file on a clipboard, however the browser chose to expose it.
 *
 * `files` is the modern list and usually enough — but some sources populate
 * only `items`, and a file whose type the OS did not label arrives with an
 * empty `type`. Both are read, because "paste sometimes does nothing" is a
 * miserable thing to debug from the outside.
 */
export function filesFromClipboard(clipboard, { images = true, video = false } = {}) {
  if (!clipboard) return [];

  const wanted = (type) =>
    (images && String(type).startsWith('image/')) || (video && String(type).startsWith('video/'));

  const out = [...(clipboard.files ?? [])].filter((f) => wanted(f.type));

  for (const item of clipboard.items ?? []) {
    if (item.kind !== 'file') continue;
    const file = item.getAsFile?.();
    if (!file) continue;
    // Already collected from `files`, in the ordinary case.
    if (out.some((f) => f.name === file.name && f.size === file.size)) continue;
    if (wanted(file.type)) out.push(file);
  }

  return out.map(named);
}

/**
 * Reads the clipboard on demand, for a Paste button.
 *
 * @returns {Promise<{files: File[], reason?: string}>} never throws — a
 *   refusal is an outcome to explain, not an exception to handle at each site
 */
export async function readClipboardImages({ video = false } = {}) {
  if (!navigator.clipboard?.read) {
    return {
      files: [],
      reason: 'This browser will not let a page read the clipboard. Press Ctrl+V instead.',
    };
  }

  try {
    const items = await navigator.clipboard.read();
    const files = [];

    for (const item of items) {
      const type = item.types.find(
        (t) => t.startsWith('image/') || (video && t.startsWith('video/')),
      );
      if (!type) continue;
      const blob = await item.getType(type);
      files.push(new File([blob], pastedName(type), { type }));
    }

    if (files.length === 0) {
      return { files: [], reason: 'There is no image on the clipboard. Copy one, then press Paste.' };
    }
    return { files };
  } catch (e) {
    // Chrome refuses without permission; Firefox refuses outright.
    return {
      files: [],
      reason: `Could not read the clipboard (${e.message}). Press Ctrl+V with this page in focus instead.`,
    };
  }
}
